import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { fetchEffectiveOnce } from '../services/uiSettingsService';
import { safeStorage } from '../utils/safeStorage';

export interface UiVisibility {
  showApiStatus: boolean;
  showDebugMenu: boolean;
  showLanguageSelector: boolean;
  /**
   * Effective allowlist of usernames for the Debug menu. Empty array means
   * "no allowlist" — fall back to `showDebugMenu`. Non-empty means only
   * users whose `preferred_username` is in the list should see the menu.
   * Consumers should AND this with their own user identity check.
   */
  debugMenuUsernames: string[];
  /**
   * Global/role visibility for the RAW tab on entity-detail modals.
   * True when every scope leaves the knob unset (preserves current
   * behaviour).
   */
  showRawTab: boolean;
  /**
   * Per-user allowlist for the RAW tab. Same semantics as
   * `debugMenuUsernames`: empty → fall back to `showRawTab`; non-empty →
   * only those usernames see the tab.
   */
  rawTabUsernames: string[];
  /**
   * Global/role visibility for the "JSON Config" block on the Config
   * Template detail modal. True when unset everywhere (preserves current
   * behaviour).
   */
  showJsonConfig: boolean;
  /**
   * Per-user allowlist for the "JSON Config" block. Same semantics as
   * `rawTabUsernames`: empty → fall back to `showJsonConfig`; non-empty →
   * only those usernames see the block.
   */
  jsonConfigUsernames: string[];
  // False until the server-side visibility settings have been resolved (or
  // skipped because the user isn't authenticated). Consumers gate the render
  // of admin-hideable items on this flag so no item flashes visible on
  // initial load only to disappear a moment later when the fetch returns.
  ready: boolean;
}

// Built-in defaults match the pre-Settings behaviour: everything visible.
// Server-side defaults (loaded via /v2/ui-settings/effective) override these
// only when an admin has explicitly set them for the caller's scope.
const DEFAULT_VISIBILITY: Omit<UiVisibility, 'ready'> = {
  showApiStatus: true,
  showDebugMenu: true,
  showLanguageSelector: true,
  debugMenuUsernames: [],
  showRawTab: true,
  rawTabUsernames: [],
  showJsonConfig: true,
  jsonConfigUsernames: [],
};

const UiVisibilityContext = createContext<UiVisibility>({ ...DEFAULT_VISIBILITY, ready: true });

// Unauthenticated users skip the server fetch entirely (see
// fetchEffectiveOnce in uiSettingsService), so their visibility is the
// built-in default and ready can flip true on the very first render — no
// header chrome flashes.
const hasAccessToken = (): boolean => {
  try {
    return !!safeStorage.getItem('access_token');
  } catch {
    return false;
  }
};

export const UiVisibilityProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [visibility, setVisibility] = useState<UiVisibility>(() => ({
    ...DEFAULT_VISIBILITY,
    ready: !hasAccessToken(),
  }));

  useEffect(() => {
    let cancelled = false;

    const applyServerDefaults = async () => {
      if (cancelled) return;
      try {
        const eff = await fetchEffectiveOnce();
        if (cancelled) return;
        const s = eff?.settings || {};
        setVisibility({
          showApiStatus: typeof s.show_api_status === 'boolean' ? s.show_api_status : true,
          showDebugMenu: typeof s.show_debug_menu === 'boolean' ? s.show_debug_menu : true,
          showLanguageSelector:
            typeof s.show_language_selector === 'boolean' ? s.show_language_selector : true,
          debugMenuUsernames: Array.isArray(s.debug_menu_usernames)
            ? s.debug_menu_usernames.filter((u): u is string => typeof u === 'string' && u.length > 0)
            : [],
          showRawTab: typeof s.show_raw_tab === 'boolean' ? s.show_raw_tab : true,
          rawTabUsernames: Array.isArray(s.raw_tab_usernames)
            ? s.raw_tab_usernames.filter((u): u is string => typeof u === 'string' && u.length > 0)
            : [],
          showJsonConfig: typeof s.show_json_config === 'boolean' ? s.show_json_config : true,
          jsonConfigUsernames: Array.isArray(s.json_config_usernames)
            ? s.json_config_usernames.filter((u): u is string => typeof u === 'string' && u.length > 0)
            : [],
          ready: true,
        });
      } catch {
        // Silent: keep built-in defaults but still flip ready so the header
        // unblocks.
        if (!cancelled) setVisibility(v => ({ ...v, ready: true }));
      }
    };

    applyServerDefaults();
    const listener = () => applyServerDefaults();
    window.addEventListener('app:auth-ready', listener);
    return () => {
      cancelled = true;
      window.removeEventListener('app:auth-ready', listener);
    };
  }, []);

  return (
    <UiVisibilityContext.Provider value={visibility}>
      {children}
    </UiVisibilityContext.Provider>
  );
};

export const useUiVisibility = (): UiVisibility => useContext(UiVisibilityContext);
